import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, ArrowLeft, Loader2, CalendarCheck, Check } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { CALENDAR_URL_STANDARD } from "../mock";
import { BookingButton } from "../components/BookingButton";
import { track } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STEP_TITLES = [
  "Your Contact Info",
  "About You",
  "Your Stage Goals",
  "Fitness & Nutrition",
  "Lifestyle & Recovery",
  "Commitment & Investment",
];
const TOTAL = STEP_TITLES.length;

const emptyState = {
  full_name: "",
  email: "",
  phone: "",
  age: "",
  city_state: "",
  division: "",
  timeframe: "",
  competed_before: "",
  past_experience: "",
  resistance_days: "",
  cardio_days: "",
  tracking_macros: "",
  nutrition_approach: "",
  injuries: "",
  stress_level: "",
  sleep_hours: "",
  metabolism_shifts: "",
  hormone_provider: "",
  commit_protocol: "",
  aware_investment: "",
  why_stage: "",
};

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const TextField = ({ label, id, type = "text", value, onChange, placeholder, optional, min, max }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-sm font-semibold text-white/80">
      {label} {optional && <span className="text-white/45 font-normal">(optional)</span>}
    </Label>
    <Input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      min={min}
      max={max}
      data-testid={`field-${id}`}
      className="bg-white/[0.03] border-white/15 text-white h-12 rounded-xl"
    />
  </div>
);

const AreaField = ({ label, id, value, onChange, placeholder, optional }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-sm font-semibold text-white/80">
      {label} {optional && <span className="text-white/45 font-normal">(optional)</span>}
    </Label>
    <Textarea
      id={id}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={4}
      data-testid={`field-${id}`}
      className="bg-white/[0.03] border-white/15 text-white rounded-xl min-h-[110px]"
    />
  </div>
);

const RadioField = ({ label, name, options, value, onChange }) => (
  <div className="space-y-2">
    <Label className="text-sm font-semibold text-white/80 block">{label}</Label>
    <RadioGroup value={value} onValueChange={onChange} className="space-y-2">
      {options.map((o) => (
        <label
          key={o}
          className={`flex items-center gap-3 min-h-[44px] px-4 rounded-xl cursor-pointer border transition-colors ${
            value === o ? "bg-primary/10 border-primary/40" : "bg-white/[0.02] border-white/8 hover:border-white/20"
          }`}
        >
          <RadioGroupItem
            value={o}
            className="border-white/30 text-primary"
            data-testid={`${name}-${slug(o)}`}
          />
          <span className="text-[15px] text-white/85 font-medium">{o}</span>
        </label>
      ))}
    </RadioGroup>
  </div>
);

const CompetitionApply = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState(emptyState);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = "Competition Coaching Application | Sassy Full Stack Fitness";
    window.scrollTo(0, 0);
    try {
      const s = sessionStorage.getItem("comp_app_state");
      const st = sessionStorage.getItem("comp_app_step");
      if (s) setData({ ...emptyState, ...JSON.parse(s) });
      if (st) setStep(Number(st));
    } catch (e) {}
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem("comp_app_state", JSON.stringify(data));
      sessionStorage.setItem("comp_app_step", String(step));
    } catch (e) {}
  }, [data, step]);

  const update = (patch) => {
    setError("");
    setData((d) => ({ ...d, ...patch }));
  };

  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const validate = (s) => {
    if (s === 1) {
      if (!data.full_name.trim()) return "Please enter your full name.";
      if (!EMAIL_RE.test(data.email.trim())) return "Please enter a valid email address.";
      if (data.phone.replace(/\D/g, "").length < 10) return "Please enter a valid cell number.";
    } else if (s === 2) {
      if (!data.age.trim()) return "Please enter your age.";
      if (!data.city_state.trim()) return "Please enter your city & state.";
    } else if (s === 3) {
      if (!data.division) return "Please choose a division.";
      if (!data.timeframe.trim()) return "Please share your show or timeframe.";
      if (!data.competed_before) return "Please let us know if you've competed before.";
      if (data.competed_before === "Yes" && !data.past_experience.trim())
        return "Please briefly describe your past experience.";
    } else if (s === 4) {
      if (!data.resistance_days) return "Please select your resistance training days.";
      if (!data.cardio_days.trim()) return "Please enter your cardio days per week.";
      if (!data.tracking_macros) return "Please answer the macro tracking question.";
      if (!data.nutrition_approach.trim()) return "Please describe your nutrition approach.";
      if (!data.injuries.trim()) return "Please note any injuries (type “None” if none).";
    } else if (s === 5) {
      if (!data.stress_level.trim()) return "Please rate your stress level (1-10).";
      const stress = Number(data.stress_level);
      if (Number.isNaN(stress) || stress < 1 || stress > 10)
        return "Stress level must be a number between 1 and 10.";
      if (!data.sleep_hours.trim()) return "Please enter your average sleep hours.";
      if (!data.metabolism_shifts.trim()) return "Please answer the metabolism question.";
    } else if (s === 6) {
      if (!data.commit_protocol) return "Please answer the commitment question.";
      if (!data.aware_investment) return "Please answer the investment question.";
      if (!data.why_stage.trim()) return "Please tell Sandra why the stage matters to you.";
    }
    return "";
  };

  const next = () => {
    const msg = validate(step);
    if (msg) {
      setError(msg);
      return;
    }
    setStep((s) => Math.min(TOTAL, s + 1));
    scrollTop();
  };

  const back = () => {
    setError("");
    setStep((s) => Math.max(1, s - 1));
    scrollTop();
  };

  const submit = async () => {
    const msg = validate(6);
    if (msg) {
      setError(msg);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/competition-application`, {
        ...data,
        origin: window.location.origin,
      });
      sessionStorage.removeItem("comp_app_state");
      sessionStorage.removeItem("comp_app_step");
      setSubmitted(true);
      track("generate_lead", { form_type: "competition_application" }, "Lead");
      scrollTop();
    } catch (err) {
      setLoading(false);
      setError("Something went wrong submitting your application. Please try again.");
    }
  };

  const firstName = data.full_name.trim().split(" ")[0] || "";

  return (
    <div className="bg-background min-h-screen flex flex-col" data-testid="competition-apply-page">
      <Header minimal />
      <main className="flex-1 pt-24 md:pt-24 pb-20 px-[5%]">
        <div className="max-w-[840px] mx-auto">
          {submitted ? (
            <div
              className="text-center bg-card/50 border border-white/10 rounded-[2rem] p-8 md:p-12 backdrop-blur-sm"
              data-testid="apply-success"
            >
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/15 text-primary">
                <Check size={32} strokeWidth={3} />
              </span>
              <h1 className="mt-6 font-display font-black uppercase tracking-tighter text-3xl md:text-4xl text-white">
                Application <span className="text-gold-gradient">Received</span>
              </h1>
              <p className="mt-4 text-base md:text-lg text-white/70 leading-relaxed max-w-lg mx-auto">
                {firstName ? `Thank you, ${firstName}. ` : "Thank you. "}
                Sandra personally reviews every application. The next step is to book your call so
                she can talk through your prep and see if it's the right fit.
              </p>
              <p className="mt-6 font-display font-black uppercase tracking-tighter text-xl md:text-2xl text-white">
                Take It Back. No Apologies.
              </p>
              <div className="mt-8">
                <BookingButton
                  href={CALENDAR_URL_STANDARD}
                  target="_blank"
                  rel="noopener noreferrer"
                  testid="apply-booking-cta"
                  context={{ name: data.full_name, email: data.email, application: true }}
                  onClick={() => track("book_call_click", { location: "competition_apply" })}
                />
              </div>
              <button
                onClick={() => navigate("/services/competition-coaching")}
                className="mt-8 inline-flex items-center gap-2 text-xs font-semibold tracking-[0.15em] uppercase text-white/50 hover:text-white/80 transition-colors focus-ring rounded-sm"
                data-testid="apply-back-to-page"
              >
                <ArrowLeft size={14} /> Back to Competition Coaching
              </button>
            </div>
          ) : (
            <>
              {/* Hero */}
              <div className="text-center">
                <button
                  onClick={() => navigate("/services/competition-coaching")}
                  className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.15em] uppercase text-white/50 hover:text-white/80 transition-colors mb-6 focus-ring rounded-sm"
                  data-testid="apply-back"
                >
                  <ArrowLeft size={14} /> Competition Coaching
                </button>
                <span className="text-[11px] font-bold tracking-[0.35em] uppercase text-primary block">
                  Competition Coaching
                </span>
                <h1 className="mt-3 font-display font-black uppercase leading-[1.05] text-[30px] md:text-[46px] text-white">
                  <span className="text-gold-gradient">The Application</span>
                </h1>
                <p className="mt-4 text-base md:text-lg text-white/70 font-medium max-w-xl mx-auto">
                  Tell Sandra about you and your goals. Takes about three minutes, then you'll book
                  your call.
                </p>
              </div>

              {/* Card */}
              <div className="mt-8 bg-card/50 border border-white/10 rounded-[2rem] p-6 md:p-10 backdrop-blur-sm min-h-[440px]">
                {/* Progress */}
                <div className="flex items-center justify-between mb-8">
                  <span className="text-xs font-bold tracking-[0.2em] uppercase text-primary" data-testid="apply-step-label">
                    Step {step} of {TOTAL} · {STEP_TITLES[step - 1]}
                  </span>
                  <div className="hidden sm:flex gap-2">
                    {Array.from({ length: TOTAL }).map((_, i) => (
                      <span
                        key={i}
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          i + 1 <= step ? "w-7 bg-primary" : "w-3.5 bg-white/15"
                        }`}
                      />
                    ))}
                  </div>
                </div>

                {loading ? (
                  <div className="flex flex-col items-center justify-center py-24 text-center" aria-live="polite">
                    <Loader2 className="text-primary animate-spin" size={40} />
                    <p className="mt-6 font-display font-bold uppercase tracking-tight text-xl text-white">
                      Sending your application…
                    </p>
                  </div>
                ) : (
                  <>
                    {step === 1 && (
                      <div className="space-y-6">
                        <TextField label="Full name" id="full_name" value={data.full_name} onChange={(e) => update({ full_name: e.target.value })} placeholder="Your full name" />
                        <TextField label="Email address" id="email" type="email" value={data.email} onChange={(e) => update({ email: e.target.value })} placeholder="you@email.com" />
                        <TextField label="Cell number" id="phone" type="tel" value={data.phone} onChange={(e) => update({ phone: e.target.value })} placeholder="(555) 555-5555" />
                        <p className="text-xs text-white/50 leading-relaxed">
                          Sandra uses these to reach you about your application. Message &amp; data rates
                          may apply.
                        </p>
                      </div>
                    )}

                    {step === 2 && (
                      <div className="space-y-6">
                        <TextField label="Age" id="age" type="number" min="18" max="99" value={data.age} onChange={(e) => update({ age: e.target.value })} placeholder="e.g., 48" />
                        <TextField label="City & State" id="city_state" value={data.city_state} onChange={(e) => update({ city_state: e.target.value })} placeholder="e.g., Austin, TX" />
                      </div>
                    )}

                    {step === 3 && (
                      <div className="space-y-7">
                        <RadioField label="Which division are you most interested in competing in?" name="division" options={["NPC Bikini", "NPC Fit Model", "Undecided (help me choose!)"]} value={data.division} onChange={(v) => update({ division: v })} />
                        <TextField label="Do you have a specific show or timeframe in mind?" id="timeframe" value={data.timeframe} onChange={(e) => update({ timeframe: e.target.value })} placeholder="e.g., Spring 2027, 6 months from now, no set date" />
                        <RadioField label="Have you ever competed in a bodybuilding or fitness show before?" name="competed_before" options={["Yes", "No"]} value={data.competed_before} onChange={(v) => update(v === "No" ? { competed_before: v, past_experience: "" } : { competed_before: v })} />
                        {data.competed_before === "Yes" && (
                          <AreaField label="Please briefly describe your past experience, federations, and placements." id="past_experience" value={data.past_experience} onChange={(e) => update({ past_experience: e.target.value })} placeholder="Federations, shows, placements…" />
                        )}
                      </div>
                    )}

                    {step === 4 && (
                      <div className="space-y-7">
                        <RadioField label="How many days per week are you currently resistance training?" name="resistance_days" options={["0-2", "3-4", "5+"]} value={data.resistance_days} onChange={(v) => update({ resistance_days: v })} />
                        <TextField label="How many days per week are you currently doing cardio?" id="cardio_days" type="number" min="0" max="14" value={data.cardio_days} onChange={(e) => update({ cardio_days: e.target.value })} placeholder="e.g., 3" />
                        <RadioField label="Are you currently tracking your macronutrients or calories?" name="tracking_macros" options={["Yes, strictly", "Yes, loosely", "No"]} value={data.tracking_macros} onChange={(v) => update({ tracking_macros: v })} />
                        <AreaField label="Please describe your current daily approach to nutrition." id="nutrition_approach" value={data.nutrition_approach} onChange={(e) => update({ nutrition_approach: e.target.value })} placeholder="A typical day of eating and how you plan it…" />
                        <AreaField label="Do you have any prior or current physical injuries or limitations Sandra should be aware of?" id="injuries" value={data.injuries} onChange={(e) => update({ injuries: e.target.value })} placeholder="Type “None” if none apply." />
                      </div>
                    )}

                    {step === 5 && (
                      <div className="space-y-7">
                        <p className="text-sm text-white/55 leading-relaxed">Competing requires intense dedication.</p>
                        <TextField label="On a scale of 1-10, how would you rate your current daily stress levels?" id="stress_level" type="number" min="1" max="10" value={data.stress_level} onChange={(e) => update({ stress_level: e.target.value })} placeholder="1 – 10" />
                        <TextField label="How many hours of quality sleep do you average per night?" id="sleep_hours" type="number" min="0" max="24" value={data.sleep_hours} onChange={(e) => update({ sleep_hours: e.target.value })} placeholder="e.g., 7" />
                        <AreaField label="As a woman over 40, have you noticed any recent shifts in your metabolism, energy, or recovery that you want to proactively address during prep?" id="metabolism_shifts" value={data.metabolism_shifts} onChange={(e) => update({ metabolism_shifts: e.target.value })} placeholder="Share what you've noticed…" />
                        <AreaField label="Are you currently working with a provider for hormone optimization, or do you have any metabolic conditions to note?" id="hormone_provider" value={data.hormone_provider} onChange={(e) => update({ hormone_provider: e.target.value })} placeholder="Optional" optional />
                      </div>
                    )}

                    {step === 6 && (
                      <div className="space-y-7">
                        <p className="text-sm text-white/55 leading-relaxed">
                          Contest prep requires a minimum of 16–24 weeks of strict, unwavering adherence.
                        </p>
                        <RadioField label="Are you prepared to commit to a highly structured training and nutrition protocol for this duration?" name="commit_protocol" options={["Yes", "No"]} value={data.commit_protocol} onChange={(v) => update({ commit_protocol: v })} />
                        <RadioField label="Are you aware of the financial investments associated with competing (coaching, NPC registration, custom suit, posing shoes, stage tanning, travel) and prepared for them?" name="aware_investment" options={["Yes", "No"]} value={data.aware_investment} onChange={(v) => update({ aware_investment: v })} />
                        <AreaField label="Why is stepping on stage important to you at this phase in your life, and why do you want Sandra as your coach?" id="why_stage" value={data.why_stage} onChange={(e) => update({ why_stage: e.target.value })} placeholder="Tell Sandra your why…" />
                      </div>
                    )}

                    {error && (
                      <p className="mt-6 text-sm font-semibold text-red-400" role="alert" data-testid="apply-error">
                        {error}
                      </p>
                    )}

                    <div className="mt-8 flex items-center gap-4">
                      {step > 1 && (
                        <button
                          onClick={back}
                          className="inline-flex items-center gap-1 text-white/60 hover:text-white font-semibold text-sm focus-ring rounded-md px-2 py-2"
                          data-testid="apply-back-btn"
                        >
                          <ChevronLeft size={18} /> Back
                        </button>
                      )}
                      <button
                        onClick={step === TOTAL ? submit : next}
                        className="ml-auto inline-flex items-center rounded-full bg-primary text-primary-foreground font-bold px-8 py-3.5 hover:brightness-110 active:scale-[0.98] transition focus-ring shadow-[0_10px_30px_-8px_rgba(212,175,55,0.6)]"
                        data-testid={step === TOTAL ? "apply-submit" : "apply-continue"}
                      >
                        {step === TOTAL ? "Submit Application" : "Continue"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CompetitionApply;
